// Pure decisions for the promo nudges (0.12.3), kept out of the container so
// they can be unit-tested without a browser.
//
// Four small asks that help Slowed grow, in one rotation: subscribe to Slowed
// on YouTube, send feedback, review it on the store, share it with a friend.
// They use the same card as the rating ask and the product updates banner,
// and they yield to both: one card at a time, the update note first, then
// the rating ask, then one of these.
//
// Never a nag: nothing shows in the first `afterMinutes` of real listening,
// never at the cap or on a lock, a show waits `cooldownDays` before any next
// nudge, each nudge shows at most `maxShows` times, "Not now" puts that one
// away for `snoozeDays` and moves the rotation on, and the button ends that
// nudge for good.
//
// The record lives in user data as `promoNudges`, next to `ratePrompt`:
// { lastShownAt, lastNudge, nudges: { [id]: { shownCount, lastShownAt,
// snoozedUntil, completedAt } } }. It is additive and tolerant of being
// missing: versions that predate it never read or write the key.

import { buildFeedbackUrl } from "./feedback-url";

const DAY_MS = 24 * 60 * 60 * 1000;

export const PROMO_NUDGE_ORDER = ["youtube", "feedback", "review", "share"];

const CONTENT = {
  youtube: { text: "Subscribe to Slowed on YouTube", cta: "Subscribe" },
  feedback: { text: "Tell us what you think", cta: "Send feedback" },
  review: { text: "Enjoying Slowed? Leave a review", cta: "Review" },
  share: { text: "Share Slowed with a friend", cta: "Share" },
};

// What the card says once the share link has gone to the clipboard.
export const SHARE_COPIED_CTA = "Link copied";

function toTime(value) {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function recordOf(promoNudges, nudge) {
  return promoNudges?.nudges?.[nudge] || {};
}

// Whether a nudge has somewhere to go and is allowed at all, before any
// count or date is looked at.
export function isPromoNudgeAvailable(
  nudge,
  {
    youtubeChannelUrl,
    feedbackUrl,
    isFeedbackEnabled,
    reviewUrl,
    isRateAppEnabled,
    ratePrompt,
    trialMode,
    trialEnded,
    shareUrl,
  } = {},
) {
  switch (nudge) {
    case "youtube":
      return Boolean(youtubeChannelUrl);
    case "feedback":
      return isFeedbackEnabled === true && Boolean(feedbackUrl);
    case "review":
      // The rating ask already covers heavy use, and whoever answered it is
      // never asked twice. A trial is days from "trial ended", which earns
      // one-star reviews, not five.
      if (isRateAppEnabled !== true || !reviewUrl) return false;
      if (ratePrompt?.completedAt) return false;
      if (trialMode === true && trialEnded !== true) return false;
      return true;
    case "share":
      return Boolean(shareUrl);
    default:
      return false;
  }
}

// The nudge to show now, or null. The rotation starts after the one shown
// last, so each ask gets its turn before any comes round again.
export function computePromoNudge({
  updateBannerVisible,
  ratePromptVisible,
  usageHit,
  musicProOnly,
  notSupported,
  reportedMinutes,
  afterMinutes = 60,
  maxShows = 2,
  cooldownDays = 5,
  promoNudges,
  // The nudge already on screen, kept through a refresh of the panel: its
  // show was counted once, and it should not vanish mid-read.
  shownNudge = null,
  now = Date.now(),
  ...availability
} = {}) {
  if (updateBannerVisible === true || ratePromptVisible === true) return null;
  if (usageHit === true || musicProOnly === true || notSupported === true) {
    return null;
  }
  if (shownNudge && PROMO_NUDGE_ORDER.includes(shownNudge)) return shownNudge;
  if ((Number(reportedMinutes) || 0) < (Number(afterMinutes) || 0)) return null;
  const lastShownAt = toTime(promoNudges?.lastShownAt);
  if (lastShownAt && now - lastShownAt < (Number(cooldownDays) || 0) * DAY_MS) {
    return null;
  }

  const count = PROMO_NUDGE_ORDER.length;
  const lastIndex = PROMO_NUDGE_ORDER.indexOf(promoNudges?.lastNudge);
  for (let step = 1; step <= count; step += 1) {
    const nudge = PROMO_NUDGE_ORDER[(lastIndex + step) % count];
    if (!isPromoNudgeAvailable(nudge, availability)) continue;
    const record = recordOf(promoNudges, nudge);
    if (record.completedAt) continue;
    if ((Number(record.shownCount) || 0) >= (Number(maxShows) || 0)) continue;
    if (toTime(record.snoozedUntil) > now) continue;
    return nudge;
  }
  return null;
}

function withRecord(promoNudges, nudge, patch) {
  return {
    ...(promoNudges || {}),
    nudges: {
      ...(promoNudges?.nudges || {}),
      [nudge]: { ...recordOf(promoNudges, nudge), ...patch },
    },
  };
}

// Persisted the moment the card is rendered, so a popup that is simply
// closed still counts as one of the few shows, and starts the cooldown.
export function markPromoNudgeShown(promoNudges, nudge, { now = Date.now() } = {}) {
  const at = new Date(now).toISOString();
  const record = recordOf(promoNudges, nudge);
  return {
    ...withRecord(promoNudges, nudge, {
      shownCount: (Number(record.shownCount) || 0) + 1,
      lastShownAt: at,
    }),
    lastShownAt: at,
    lastNudge: nudge,
  };
}

// "Not now": that nudge waits `days`; the rotation has already moved on.
export function snoozePromoNudge(
  promoNudges,
  nudge,
  { days = 30, now = Date.now() } = {},
) {
  return withRecord(promoNudges, nudge, {
    snoozedUntil: new Date(now + days * DAY_MS).toISOString(),
  });
}

// The button: that nudge is never shown again.
export function completePromoNudge(promoNudges, nudge, { now = Date.now() } = {}) {
  return withRecord(promoNudges, nudge, {
    completedAt: new Date(now).toISOString(),
  });
}

// What the card says for `nudge`, or null for an unknown one.
export function resolvePromoNudgeContent(nudge, { copied = false } = {}) {
  const content = CONTENT[nudge];
  if (!content) return null;
  if (nudge === "share" && copied) return { ...content, cta: SHARE_COPIED_CTA };
  return content;
}

// Where the button goes, or null when the config has no address for it.
export function buildPromoNudgeUrl(
  nudge,
  { youtubeChannelUrl, feedbackUrl, reviewUrl, rateAppUrl, shareUrl } = {},
) {
  try {
    switch (nudge) {
      case "youtube":
        return youtubeChannelUrl || null;
      case "feedback":
        return feedbackUrl ? buildFeedbackUrl(feedbackUrl, "general") : null;
      case "review":
        return reviewUrl || rateAppUrl || null;
      case "share":
        return shareUrl || null;
      default:
        return null;
    }
  } catch (_) {
    return null;
  }
}
